import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useProviderStore } from 'core/store/providerStore';
import {
  getMyDriverProfiles,
  getMyLicenceStatus,
  submitDriverVerification,
  uploadDriverLicenceDocumentAndWait,
} from 'features/drivers/services/driverApi';
import { formatDriverApiError } from 'features/drivers/lib/driverErrors';
import { formatVehicleType } from 'features/drivers/lib/driverLicenceStatus';
import { queryKeys } from 'core/query/queryKeys';
import { invalidateFleetQueries } from 'core/query/invalidateFleet';
import { ROUTES } from 'core/router/routes';
import LicenceStatusGrid from 'shared/components/provider/LicenceStatusGrid';
import OrgSwitcher from 'shared/components/provider/OrgSwitcher';
import type { DriverLicenceRow, DriversMeResponse } from 'types/api.generated';

export default function DriverLicence() {
  const queryClient = useQueryClient();
  const { activeOrgId, orgMemberships, setActiveOrgId } = useProviderStore();

  const profilesQuery = useQuery<DriversMeResponse>({
    queryKey: queryKeys.drivers.me,
    queryFn: getMyDriverProfiles,
  });

  const driverOrgIds = useMemo(
    () => profilesQuery.data?.profiles.map((p) => p.organizationId) ?? [],
    [profilesQuery.data],
  );

  const organizationId = useMemo(() => {
    if (activeOrgId && driverOrgIds.includes(activeOrgId)) return activeOrgId;
    if (driverOrgIds[0]) return driverOrgIds[0];
    return activeOrgId ?? orgMemberships[0]?.orgId ?? '';
  }, [activeOrgId, driverOrgIds, orgMemberships]);

  useEffect(() => {
    if (
      organizationId &&
      driverOrgIds.includes(organizationId) &&
      organizationId !== activeOrgId
    ) {
      setActiveOrgId(organizationId);
    }
  }, [organizationId, driverOrgIds, activeOrgId, setActiveOrgId]);

  const [submitRow, setSubmitRow] = useState<DriverLicenceRow | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const licenceQuery = useQuery({
    queryKey: queryKeys.drivers.licenceStatus(organizationId),
    queryFn: () => getMyLicenceStatus(organizationId),
    enabled: Boolean(organizationId),
  });

  const submitMutation = useMutation({
    mutationFn: async ({
      row,
      documentUrl,
      expiry,
    }: {
      row: DriverLicenceRow;
      documentUrl: string;
      expiry: string;
    }) =>
      submitDriverVerification({
        organizationId,
        vehicleType: row.vehicleType,
        driversLicence: {
          documentUrl,
          expiresAt: new Date(expiry).toISOString(),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.drivers.licenceStatus(organizationId),
      });
      void invalidateFleetQueries(queryClient, { orgId: organizationId });
      setSubmitRow(null);
      setFile(null);
      setExpiresAt('');
      setFormError(null);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitRow || !file || !expiresAt || !organizationId) {
      setFormError('Licence image and expiry date are required.');
      return;
    }
    if (!submitRow.canSubmit && !submitRow.canResubmit) {
      setFormError('You cannot submit this licence right now.');
      return;
    }

    const expiryDate = new Date(expiresAt);
    if (Number.isNaN(expiryDate.getTime()) || expiryDate <= new Date()) {
      setFormError('Expiry date must be in the future.');
      return;
    }

    setFormError(null);
    setSuccessMessage(null);
    try {
      const documentUrl = await uploadDriverLicenceDocumentAndWait(
        file,
        submitRow.vehicleType,
        organizationId,
      );
      await submitMutation.mutateAsync({
        row: submitRow,
        documentUrl,
        expiry: expiresAt,
      });
      setSuccessMessage(
        `Your ${formatVehicleType(submitRow.vehicleType)} licence is under review.`,
      );
    } catch (err: unknown) {
      setFormError(formatDriverApiError(err, 'Could not submit licence. Try again.'));
    }
  };

  if (profilesQuery.isLoading) {
    return <p className="p-10 text-center text-slate-500">Loading…</p>;
  }

  if (!organizationId) {
    return (
      <p className="p-10 text-center text-slate-500">
        {driverOrgIds.length === 0
          ? 'You are not enabled as a driver for any organisation. Ask an admin to enable your driver profile.'
          : 'Select an organisation from your dashboard first.'}
      </p>
    );
  }

  if (licenceQuery.isLoading) {
    return <p className="p-10 text-center text-slate-500">Loading licence status…</p>;
  }

  if (licenceQuery.isError || !licenceQuery.data) {
    return (
      <div className="p-10 text-center space-y-4 max-w-md mx-auto">
        <p className="text-red-600">
          {formatDriverApiError(
            licenceQuery.error,
            'Could not load licence status. Try again later.',
          )}
        </p>
        <OrgSwitcher />
      </div>
    );
  }

  const { licences } = licenceQuery.data;

  return (
    <div className="min-h-screen bg-background-light px-6 py-12 font-display">
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-black mb-2">Driver licences</h1>
          <p className="text-sm text-slate-600">
            One licence per vehicle type. Go online from the dashboard after your
            vehicle and licence are approved.
          </p>
        </div>

        <OrgSwitcher />

        <LicenceStatusGrid
          licences={licences}
          variant="driver"
          onSubmit={(row) => {
            setSubmitRow(row);
            setFormError(null);
            setSuccessMessage(null);
            setFile(null);
            setExpiresAt('');
          }}
        />

        {successMessage && (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
            {successMessage}
          </p>
        )}

        {submitRow && (
          <form
            onSubmit={(e) => void handleSubmit(e)}
            className="bg-white rounded-2xl border p-6 space-y-4"
          >
            <h2 className="font-bold text-slate-800">
              {submitRow.canResubmit ? 'Resubmit' : 'Submit'}{' '}
              {formatVehicleType(submitRow.vehicleType)} licence
            </h2>
            {submitRow.rejectionReason && (
              <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2">
                {submitRow.rejectionReason}
              </p>
            )}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">
                Licence image
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">
                Expires
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm"
                required
              />
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="flex-1 bg-primary text-white font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit for review'}
              </button>
              <button
                type="button"
                onClick={() => setSubmitRow(null)}
                className="px-4 py-3 rounded-xl border text-sm font-bold text-slate-600"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <Link
          to={ROUTES.PROVIDER.DASHBOARD}
          className="block text-center text-sm text-primary font-semibold"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
